/**
 * Confirm dialog for an order with more than one leg.
 *
 * Shows every leg with its own verdict: ready to confirm, already confirmed,
 * cancelled, or blocked (with the reasons). The user can confirm one leg at a
 * time or all the ready ones at once. A one-leg order never sees this dialog —
 * that action confirms immediately.
 */
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ConfirmPlan } from "@/lib/confirm-plan";

interface ConfirmLegsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: ConfirmPlan;
  isPending: boolean;
  pendingLegIds: string[];
  onConfirmLegs: (legIds: string[]) => void;
}

export function ConfirmLegsDialog({
  open,
  onOpenChange,
  plan,
  isPending,
  pendingLegIds,
  onConfirmLegs,
}: ConfirmLegsDialogProps) {
  const eligibleCount = plan.eligibleLegIds.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm legs</DialogTitle>
          <DialogDescription>
            Confirm one leg at a time, or every leg that's ready. The order
            itself is marked confirmed once all of its legs are.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2">
          {plan.legs.map((leg) => {
            const busy = isPending && pendingLegIds.includes(leg.legId);
            return (
              <li
                key={leg.legId}
                className="flex items-start justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Leg {leg.legSequence}</span>
                    {leg.state === "already" && (
                      <Badge variant="secondary">Already confirmed</Badge>
                    )}
                    {leg.state === "canceled" && <Badge variant="outline">Cancelled</Badge>}
                    {leg.state === "blocked" && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Not ready
                      </Badge>
                    )}
                  </div>
                  {leg.reasons.length > 0 && (
                    <ul className="list-disc pl-4 text-xs text-muted-foreground">
                      {leg.reasons.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {leg.state === "eligible" && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isPending}
                    onClick={() => onConfirmLegs([leg.legId])}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Confirm"
                    )}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Close
          </Button>
          <Button
            disabled={isPending || eligibleCount === 0}
            onClick={() => onConfirmLegs(plan.eligibleLegIds)}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Confirm all ready ({eligibleCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
